import { Measure, useMeasurementPresentation } from '../Measurement'
import { useState, useMemo, useRef, useEffect } from 'react'
import { useTheme } from '../../context/ThemeContext'
import { Layers, Move3d, Package, Eye, EyeOff, ArrowUp, Weight, Box, Play, Pause, Ruler, ArrowRight, CheckCircle2, CircleDashed, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'
import { getPalletDisplayLabel, resolvePalletSpecifications } from '../../lib/palletPresets'
import { buildPalletPreviewLayout } from '../../lib/palletPreviewLayout'
import { buildPalletVisualParts } from '../../lib/palletVisualGeometry'
import CartonFaceDetails from './CartonFaceDetails'

interface PalletSpecification3DPreviewProps {
    palletType: string
    palletCustomLength?: number | null
    palletCustomWidth?: number | null
    maxPalletHeight?: number | null
    palletBoardHeight?: number | null
    palletEmptyWeight?: number | null
    palletColor?: string | undefined
    maxPalletWeight?: number | null
    packageLength?: number | null
    packageWidth?: number | null
    packageHeight?: number | null
    packageWeight?: number | null
    packageColor?: string | undefined
    unit?: string
    onCompleteDimensions?: () => void
    appearance?: 'theme' | 'studio'
}

// ── Helper to adjust color brightness ────────────────────────────────────────
function adjustBrightness(hexColor: string, factor: number): string {
    let hex = (hexColor || '#8B5E3C').replace('#', '')
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('')
    const channel = (offset: number, fallback: number) => {
        const value = parseInt(hex.substring(offset, offset + 2), 16)
        return Number.isFinite(value) ? value : fallback
    }
    const r = channel(0, 139)
    const g = channel(2, 94)
    const b = channel(4, 60)
    const nr = Math.min(255, Math.max(0, Math.round(r * factor)))
    const ng = Math.min(255, Math.max(0, Math.round(g * factor)))
    const nb = Math.min(255, Math.max(0, Math.round(b * factor)))
    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`
}

// ── Color palette per dimension axis ─────────────────────────────────────────
const DIM_COLORS = {
    length: { stroke: '#f97316', bg: '#fff7ed', text: '#9a3412', glow: 'rgba(249,115,22,0.35)' }, // orange
    width: { stroke: '#22c55e', bg: '#f0fdf4', text: '#15803d', glow: 'rgba(34,197,94,0.35)' }, // green
    depth: { stroke: '#a78bfa', bg: '#f5f3ff', text: '#6d28d9', glow: 'rgba(167,139,250,0.35)' }, // violet
    maxHeight: { stroke: '#0891b2', bg: '#ecfeff', text: '#155e75', glow: 'rgba(8,145,178,0.35)' }, // cyan
}
const DIM_COLORS_DARK = {
    length: { stroke: '#fb923c', bg: 'rgba(251,146,60,0.15)', text: '#fed7aa', glow: 'rgba(251,146,60,0.4)' },
    width: { stroke: '#4ade80', bg: 'rgba(74,222,128,0.15)', text: '#bbf7d0', glow: 'rgba(74,222,128,0.4)' },
    depth: { stroke: '#c4b5fd', bg: 'rgba(196,181,253,0.15)', text: '#ede9fe', glow: 'rgba(196,181,253,0.4)' },
    maxHeight: { stroke: '#22d3ee', bg: 'rgba(34,211,238,0.15)', text: '#a5f3fc', glow: 'rgba(34,211,238,0.4)' },
}

export default function PalletSpecification3DPreview({
    palletType = 'EUR',
    palletCustomLength,
    palletCustomWidth,
    maxPalletHeight,
    palletBoardHeight,
    palletEmptyWeight,
    palletColor = '#8B5E3C',
    maxPalletWeight: _maxPalletWeight,
    packageLength,
    packageWidth,
    packageHeight,
    packageWeight,
    packageColor = '#6366f1',
    unit = 'mm',
    onCompleteDimensions,
    appearance = 'theme',
}: PalletSpecification3DPreviewProps) {
    const { theme } = useTheme()
    const presentation = useMeasurementPresentation()
    const isDark = theme === 'dark' && appearance !== 'studio'

    const dimColors = isDark ? DIM_COLORS_DARK : DIM_COLORS

    // Container styling based on theme
    const containerClass = isDark
        ? 'rounded-2xl border border-slate-700/80 bg-slate-900/90 shadow-sm overflow-hidden'
        : 'rounded-2xl border border-white/80 bg-white/90 shadow-sm overflow-hidden'
    const headerTextColor = isDark ? 'text-slate-200' : 'text-slate-900'
    const svgBackground = isDark ? '#020205' : '#f6f7f5'

    // ── Pallet Dimensions ───────────────────────────────────────────────────────
    const pallet = resolvePalletSpecifications({ pallet_type: palletType,
        pallet_custom_length: palletCustomLength, pallet_custom_width: palletCustomWidth,
        pallet_board_height_mm: palletBoardHeight, pallet_empty_weight_kg: palletEmptyWeight })
    const palletL = pallet.lengthMm
    const palletW = pallet.widthMm
    const palletH = pallet.boardHeightMm
    const maxH = maxPalletHeight || 1800

    const pkgL = Number(packageLength) || 0
    const pkgW = Number(packageWidth) || 0
    const pkgH = Number(packageHeight) || 0

    // ── Camera Orbit Interaction State ──────────────────────────────────────────
    const [yaw, setYaw] = useState<number>(225)
    const [pitch, setPitch] = useState<number>(20)
    const [zoom, setZoom] = useState<number>(1.3)
    const [panX, setPanX] = useState<number>(0)
    const [panY, setPanY] = useState<number>(0)
    const [autoRotate, setAutoRotate] = useState<boolean>(false)
    const [showAnnotations, setShowAnnotations] = useState<boolean>(true)
    const [showPallet, setShowPallet] = useState<boolean>(true)
    const [showPackages, setShowPackages] = useState<boolean>(true)
    const [useKraftColor, setUseKraftColor] = useState(false)
    const [highlightedAttribute, setHighlightedAttribute] = useState<string | null>(null)
    const animRef = useRef<number | null>(null)
    const canvasRef = useRef<HTMLDivElement>(null)
    const packageSurfaceColor = useKraftColor ? '#c69b63' : packageColor

    const layout = useMemo(() => buildPalletPreviewLayout({
        palletLength: palletL, palletWidth: palletW, boardHeight: palletH, maxHeight: maxH,
        packageLength, packageWidth, packageHeight,
    }), [palletL, palletW, palletH, maxH, packageLength, packageWidth, packageHeight])
    const packagesPerLayer = layout.packagesPerLayer
    const maxLayers = layout.layers
    const packagePositions = layout.positions

    const totalWeight = useMemo(() => {
        const palletWeight = Number(pallet.emptyWeightKg)
        if (!layout.ready || packageWeight == null || !Number.isFinite(Number(packageWeight)) || Number(packageWeight) < 0) return null
        const packagesWeight = layout.totalPackages * Number(packageWeight)
        const total = palletWeight + packagesWeight
        return Number.isFinite(total) ? total : null
    }, [layout.ready, layout.totalPackages, pallet.emptyWeightKg, packageWeight])

    // Auto-rotation
    useEffect(() => {
        if (autoRotate) {
            const step = () => {
                setYaw(prev => (prev + 0.5) % 360)
                animRef.current = requestAnimationFrame(step)
            }
            animRef.current = requestAnimationFrame(step)
        } else {
            if (animRef.current) cancelAnimationFrame(animRef.current)
        }
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
    }, [autoRotate])

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const handleWheel = (e: WheelEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const delta = e.deltaY > 0 ? -0.1 : 0.1
            setZoom(prev => Math.max(0.8, Math.min(2.5, prev + delta)))
        }
        canvas.addEventListener('wheel', handleWheel, { passive: false } as any)
        return () => { canvas.removeEventListener('wheel', handleWheel) }
    }, [])

    const handlePointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0 && e.button !== 2) return
        setAutoRotate(false)
        const startX = e.clientX, startY = e.clientY
        const startYaw = yaw, startPitch = pitch
        const startPanX = panX, startPanY = panY

        const handlePointerMove = (moveEvent: PointerEvent) => {
            const dx = moveEvent.clientX - startX
            const dy = moveEvent.clientY - startY
            if (e.button === 0) {
                setYaw((startYaw - dx * 0.6 + 360) % 360)
                setPitch(Math.max(5, Math.min(60, startPitch + dy * 0.4)))
            } else if (e.button === 2) {
                setPanX(startPanX + dx * 0.5)
                setPanY(startPanY + dy * 0.5)
            }
        }
        const handlePointerUp = () => {
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', handlePointerUp)
        }
        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', handlePointerUp)
    }

    const setViewIsometric = () => { setYaw(225); setPitch(20); setZoom(1.3); setPanX(0); setPanY(0) }
    const setViewTop = () => { setYaw(180); setPitch(90); setZoom(1.3); setPanX(0); setPanY(0) }
    const setViewSide = () => { setYaw(90); setPitch(10); setZoom(1.3); setPanX(0); setPanY(0) }

    // ── 3D Projection Engine ───────────────────────────────────────────────────
    const svgWidth = 700
    const svgHeight = 420
    const cx = svgWidth / 2
    const cy = svgHeight / 2 + 20

    const maxDim = Math.max(palletL, palletW, maxH) || 1000
    const scale = (Math.min(svgWidth, svgHeight) / maxDim) * 0.35 * zoom

    const radYaw = (yaw * Math.PI) / 180
    const radPitch = (pitch * Math.PI) / 180

    const project = (x: number, y: number, z: number): [number, number, number] => {
        const x1 = x * Math.cos(radYaw) - y * Math.sin(radYaw)
        const y1 = x * Math.sin(radYaw) + y * Math.cos(radYaw)
        const sx = cx + x1 * scale + panX
        const sy = cy - (z * Math.cos(radPitch) - y1 * Math.sin(radPitch)) * scale + panY
        const depth = y1 * Math.cos(radPitch) + z * Math.sin(radPitch)
        return [sx, sy, depth]
    }

    const halfL = palletL / 2
    const halfW = palletW / 2

    const palletVertices: [number, number, number][] = [
        [-halfL, -halfW, 0], [halfL, -halfW, 0],
        [halfL, halfW, 0], [-halfL, halfW, 0],
        [-halfL, -halfW, palletH], [halfL, -halfW, palletH],
        [halfL, halfW, palletH], [-halfL, halfW, palletH],
    ]

    const pPalletVerts = useMemo(
        () => palletVertices.map(v => project(v[0], v[1], v[2])),
        [palletL, palletW, palletH, yaw, pitch, scale, cx, cy, panX, panY]
    )

    const sortedPalletFaces = useMemo(() => {
        const faces = [
            { id: 'bottom', indices: [0, 3, 2, 1], shade: 0.5 },
            { id: 'top', indices: [4, 5, 6, 7], shade: 1.15 },
            { id: 'front', indices: [0, 1, 5, 4], shade: 0.9 },
            { id: 'right', indices: [1, 2, 6, 5], shade: 0.76 },
            { id: 'back', indices: [2, 3, 7, 6], shade: 0.65 },
            { id: 'left', indices: [3, 0, 4, 7], shade: 0.84 },
        ]
        return buildPalletVisualParts(Number(palletL), Number(palletW), Number(palletH)).flatMap(part => {
            const { x, y, z, length: l, width: w, height: h } = part
            const vertices = [[x, y, z], [x + l, y, z], [x + l, y + w, z], [x, y + w, z],
                [x, y, z + h], [x + l, y, z + h], [x + l, y + w, z + h], [x, y + w, z + h]]
            const points = vertices.map(vertex => project(vertex[0], vertex[1], vertex[2]))
            return faces.map(face => ({ ...face, part, points,
                avgDepth: face.indices.reduce((sum, index) => sum + points[index][2], 0) / face.indices.length }))
        // Higher timber layers cover the layers underneath, including at shallow camera angles.
        }).sort((a, b) => a.part.z - b.part.z || a.avgDepth - b.avgDepth)
    }, [palletL, palletW, palletH, yaw, pitch, scale, cx, cy, panX, panY])

    const getPackageFaces = (px: number, py: number, pz: number) => {
        const halfPL = pkgL / 2, halfPW = pkgW / 2
        const vertices: [number, number, number][] = [
            [px - halfPL, py - halfPW, pz], [px + halfPL, py - halfPW, pz],
            [px + halfPL, py + halfPW, pz], [px - halfPL, py + halfPW, pz],
            [px - halfPL, py - halfPW, pz + pkgH], [px + halfPL, py - halfPW, pz + pkgH],
            [px + halfPL, py + halfPW, pz + pkgH], [px - halfPL, py + halfPW, pz + pkgH],
        ]
        const pVerts = vertices.map(v => project(v[0], v[1], v[2]))
        const faces = [
            { id: 'top', indices: [4, 5, 6, 7], shade: 1.15, color: packageSurfaceColor },
            { id: 'front', indices: [0, 1, 5, 4], shade: 0.95, color: packageSurfaceColor },
            { id: 'right', indices: [1, 2, 6, 5], shade: 0.8, color: packageSurfaceColor },
            { id: 'back', indices: [2, 3, 7, 6], shade: 0.76, color: packageSurfaceColor },
            { id: 'left', indices: [3, 0, 4, 7], shade: 0.86, color: packageSurfaceColor },
        ]
        return faces.map(f => {
            const avgDepth = f.indices.reduce((sum, idx) => sum + pVerts[idx][2], 0) / f.indices.length
            return { ...f, avgDepth, pVerts }
        }).sort((a, b) => a.avgDepth - b.avgDepth)
    }

    const allPackageFaces = useMemo(() => {
        const allFaces: Array<{
            id: string; indices: number[]; shade: number; color: string
            avgDepth: number; pVerts: [number, number, number][]; layer: number; ordinal: number
        }> = []
        packagePositions.forEach((pos, ordinal) => {
            getPackageFaces(pos.x, pos.y, pos.z).forEach(face => allFaces.push({ ...face, layer: pos.layer, ordinal }))
        })
        return allFaces.sort((a, b) => a.avgDepth - b.avgDepth)
    }, [packagePositions, yaw, pitch, scale, cx, cy, pkgL, pkgW, pkgH, packageSurfaceColor, panX, panY])

    // ── Dimension lines (redesigned) ──────────────────────────────────────────
    const dimensionLines = useMemo(() => {
        if (!showPallet) return []
        const [, , , , p4, p5] = pPalletVerts
        const frontRightBottom = project(halfL, -halfW, 0) // front-right-bottom
        const frontRightTop = project(halfL, -halfW, palletH) // front-right-top

        const OFFSET_L = 28
        const OFFSET_W = 60
        const OFFSET_D = 30
        const OFFSET_MH = 55

        // Length: along front edge but below the palette in white space (p4 → p5)
        const lFrom: [number, number] = [p4[0], p4[1] + OFFSET_L * 2]
        const lTo: [number, number] = [p5[0], p5[1] + OFFSET_L * 2]

        // Width: from back-left to front-left, sharing starting point with Hmax arrows
        const wBackLeft = project(-halfL, halfW, 0)
        const wFrontLeft = project(-halfL, -halfW, 0)
        const wFrom: [number, number] = [wBackLeft[0], wBackLeft[1] + OFFSET_W]
        const wTo: [number, number] = [wFrontLeft[0], wFrontLeft[1] + OFFSET_W]

        // Board height (depth): front-right vertical edge (frontRightBottom → frontRightTop)
        const dFrom: [number, number] = [frontRightBottom[0] - OFFSET_D, frontRightBottom[1]]
        const dTo: [number, number] = [frontRightTop[0] - OFFSET_D, frontRightTop[1]]

        // Max height: use back-left corner which is vertically aligned
        const mhLeftBase = project(-halfL, halfW, 0)
        const mhLeftTop = project(-halfL, halfW, maxH)
        const mhFrom: [number, number] = [mhLeftBase[0] + OFFSET_MH, mhLeftBase[1]]
        const mhTo: [number, number] = [mhLeftTop[0] + OFFSET_MH, mhLeftTop[1]]

        interface DimLine {
            id: keyof typeof DIM_COLORS
            from: [number, number]
            to: [number, number]
            label: string
            extFrom: [number, number, number]
            extTo: [number, number, number]
            vertical?: boolean
        }

        const lines: DimLine[] = [
            {
                id: 'length', from: lFrom, to: lTo,
                label: `L: ${presentation.format(palletL, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}`,
                extFrom: p4, extTo: p5,
            },
            {
                id: 'width', from: wFrom, to: wTo,
                label: `l: ${presentation.format(palletW, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}`,
                extFrom: wBackLeft, extTo: wFrontLeft,
            },
            {
                id: 'depth', from: dFrom, to: dTo,
                label: `P: ${presentation.format(palletH, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}`,
                extFrom: frontRightBottom, extTo: frontRightTop, vertical: true,
            },
            {
                id: 'maxHeight', from: mhFrom, to: mhTo,
                label: `Hmax: ${presentation.format(maxH, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}`,
                extFrom: mhLeftBase, extTo: mhLeftTop, vertical: true,
            },
        ]

        // Total stacked height when toggled
        if (showPackages && packagePositions.length > 0 && highlightedAttribute === 'height') {
            const totalHeight = palletH + maxLayers * pkgH
            const bp = project(-halfL, halfW, 0)
            const tp = project(-halfL, halfW, totalHeight)
            lines.push({
                id: 'maxHeight',
                from: [bp[0] + OFFSET_MH, bp[1]],
                to: [tp[0] + OFFSET_MH, tp[1]],
                label: `${presentation.format(totalHeight, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}`,
                extFrom: bp, extTo: tp, vertical: true,
            })
        }

        return lines
    }, [showPallet, pPalletVerts, palletL, palletW, palletH, maxH, unit, halfL, halfW,
        showPackages, highlightedAttribute, maxLayers, pkgH, presentation, packagePositions.length])

    const ceilingOverlay = useMemo(() => {
        if (!showPackages) return null
        const ceilingZ = maxH
        const cVerts = [
            [-halfL, -halfW, ceilingZ], [halfL, -halfW, ceilingZ],
            [halfL, halfW, ceilingZ], [-halfL, halfW, ceilingZ],
        ].map(v => project(v[0] as number, v[1] as number, v[2] as number))
        const pts = cVerts.map(v => `${v[0]},${v[1]}`).join(' ')
        return (
            <polygon
                points={pts}
                fill={isDark ? 'rgba(8,145,178,0.06)' : 'rgba(8,145,178,0.09)'}
                stroke={isDark ? 'rgba(34,211,238,0.45)' : 'rgba(8,145,178,0.55)'}
                strokeWidth={1.5} strokeDasharray="6,4" opacity={0.8}
            />
        )
    }, [showPackages, maxH, halfL, halfW, yaw, pitch, scale, cx, cy, panX, panY, isDark])

    const handleAttributeHover = (attribute: string) => setHighlightedAttribute(attribute)
    const handleAttributeLeave = () => setHighlightedAttribute(null)

    // ── Render dimension arrow with label pill ────────────────────────────────
    const renderDimensionArrow = (
        line: { id: keyof typeof DIM_COLORS; from: [number, number]; to: [number, number]; label: string; extFrom: [number, number, number]; extTo: [number, number, number]; vertical?: boolean },
        idx: number
    ) => {
        const clr = dimColors[line.id]
        const mid: [number, number] = [(line.from[0] + line.to[0]) / 2, (line.from[1] + line.to[1]) / 2]

        // Pill offset: place it outside the arrow line
        const isVert = line.vertical
        const isLength = line.id === 'length'
        const isWidth = line.id === 'width'
        const isMaxHeight = line.id === 'maxHeight'
        // Estimate pill width from label length
        const pW = line.label.length * 6.2 + 14
        const pillX = isVert ? (isMaxHeight ? mid[0] : mid[0] - 48) : (isLength ? mid[0] - pW / 2 : (isWidth ? mid[0] + pW / 2 : mid[0]))
        const pillY = isVert ? mid[1] : line.from[1] + 16

        // Tick length
        const TICK = 8

        // Direction vector for perpendicular ticks
        const dx = line.to[0] - line.from[0]
        const dy = line.to[1] - line.from[1]
        const len = Math.sqrt(dx * dx + dy * dy) || 1
        const px = -dy / len  // perpendicular unit x
        const py = dx / len  // perpendicular unit y

        // Arrow path: double-headed with ticks at both ends
        const arrowId = `arrow-${line.id}-${idx}`

        return (
            <g key={arrowId}>
                {/* Extension leaders (from model edge → dim line) */}
                <line
                    x1={line.extFrom[0]} y1={line.extFrom[1]}
                    x2={line.from[0]} y2={line.from[1]}
                    stroke={clr.stroke} strokeWidth={1} strokeDasharray="4,3" opacity={0.6}
                />
                <line
                    x1={line.extTo[0]} y1={line.extTo[1]}
                    x2={line.to[0]} y2={line.to[1]}
                    stroke={clr.stroke} strokeWidth={1} strokeDasharray="4,3" opacity={0.6}
                />

                {/* Dim line with arrowheads via marker */}
                <defs>
                    <marker
                        id={`ah-${line.id}-${idx}`}
                        viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="5" markerHeight="5"
                        orient="auto-start-reverse" markerUnits="strokeWidth"
                    >
                        <path d="M 0 1 L 9 5 L 0 9 Z" fill={clr.stroke} />
                    </marker>
                </defs>

                {/* Glow / halo behind the dim line */}
                <line
                    x1={line.from[0]} y1={line.from[1]}
                    x2={line.to[0]} y2={line.to[1]}
                    stroke={clr.glow} strokeWidth={6} opacity={0.4} strokeLinecap="round"
                />
                {/* Main arrow line */}
                <line
                    x1={line.from[0]} y1={line.from[1]}
                    x2={line.to[0]} y2={line.to[1]}
                    stroke={clr.stroke}
                    strokeWidth={2.2}
                    markerStart={`url(#ah-${line.id}-${idx})`}
                    markerEnd={`url(#ah-${line.id}-${idx})`}
                    opacity={1}
                    strokeLinecap="round"
                />

                {/* Tick marks at both ends */}
                <line
                    x1={line.from[0] + px * TICK} y1={line.from[1] + py * TICK}
                    x2={line.from[0] - px * TICK} y2={line.from[1] - py * TICK}
                    stroke={clr.stroke} strokeWidth={2} opacity={0.9}
                />
                <line
                    x1={line.to[0] + px * TICK} y1={line.to[1] + py * TICK}
                    x2={line.to[0] - px * TICK} y2={line.to[1] - py * TICK}
                    stroke={clr.stroke} strokeWidth={2} opacity={0.9}
                />

                {/* Label pill (rounded rect + text) */}
                {(() => {
                    const pH = 18
                    return (
                        <>
                            {/* Pill shadow */}
                            <rect
                                x={pillX - pW / 2 + 1} y={pillY - pH / 2 + 1}
                                width={pW} height={pH} rx={9}
                                fill="rgba(0,0,0,0.18)"
                            />
                            {/* Pill background */}
                            <rect
                                x={pillX - pW / 2} y={pillY - pH / 2}
                                width={pW} height={pH} rx={9}
                                fill={clr.bg}
                                stroke={clr.stroke} strokeWidth={1.2}
                                opacity={0.97}
                            />
                            {/* Label text */}
                            <text
                                x={pillX} y={pillY}
                                fill={clr.text}
                                fontSize="10.5" fontWeight="700"
                                textAnchor="middle" dominantBaseline="central"
                                style={{ fontFamily: "'IBM Plex Mono', 'Courier New', monospace", letterSpacing: '0.4px' }}
                            >
                                {line.label}
                            </text>
                        </>
                    )
                })()}
            </g>
        )
    }

    return (
        <div role="region" aria-label="Aperçu de palettisation" className={containerClass}>
            <div className="flex items-start justify-between gap-3 px-4 pt-5 sm:px-5">
                <div className="min-w-0">
                    <div className="mb-1 inline-flex items-center gap-1.5 text-[10px] text-slate-400"><Package size={11} />Configuration palette</div>
                    <h3 className={`text-lg font-semibold tracking-tight ${headerTextColor}`}>{getPalletDisplayLabel(palletType, { length: palletL, width: palletW })}</h3>
                    <p className="mt-1 text-[11px] text-slate-400"><Measure value={palletL} from="mm" quantity="cargoLength" /> × <Measure value={palletW} from="mm" quantity="cargoLength" /></p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <div role="group" aria-label="Teinte des cartons" className="inline-flex rounded-full bg-stone-100 p-1">
                        {[{ enabled: true, label: 'Kraft', title: 'Teinte kraft' }, { enabled: false, label: 'Couleur', title: 'Couleur du produit' }].map(mode => <button key={mode.label} type="button" title={mode.title} aria-label={mode.title} aria-pressed={useKraftColor === mode.enabled} onClick={() => setUseKraftColor(mode.enabled)} className={`rounded-full px-2.5 py-1.5 text-[10px] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${useKraftColor === mode.enabled ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>{mode.label}</button>)}
                    </div>
                    <button type="button" title="Réinitialiser la vue" aria-label="Réinitialiser la vue" onClick={() => { setAutoRotate(false); setViewIsometric() }} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-stone-100 text-slate-500 transition hover:bg-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"><RotateCcw size={15} /></button>
                </div>
            </div>
            {!layout.ready && <div className="m-4 flex items-start gap-3 rounded-xl p-4 sm:p-5" style={{
                background: isDark ? 'linear-gradient(135deg, #241c12, #1c1917)' : 'linear-gradient(135deg, #fffbeb, #fff7ed)',
                border: `1px solid ${isDark ? '#785328' : '#fed7aa'}`,
            }}>
                <div aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{
                    background: isDark ? '#422b12' : '#ffedd5', color: isDark ? '#fbbf24' : '#b45309',
                }}><Ruler size={20} /></div>
                <div className="min-w-0 flex-1">
                    <div role="status" aria-live="polite">
                        <h3 className="text-sm font-semibold leading-6" style={{ color: isDark ? '#fde68a' : '#78350f' }}>
                            {layout.reason === 'package-dimensions' ? 'Dimensions du colis à compléter' : 'Vérifiez les dimensions'}
                        </h3>
                        <p className="mt-1 text-xs leading-5" style={{ color: isDark ? '#d6bc95' : '#92400e' }}>
                            {layout.reason === 'package-dimensions'
                                ? 'Ajoutez les dimensions manquantes dans « Dimensions & Poids » pour visualiser vos colis sur la palette.'
                                : 'Les dimensions saisies ne permettent pas de générer l’aperçu. Vérifiez celles du colis et de la palette.'}
                        </p>
                        {layout.reason === 'package-dimensions' && <ul aria-label="Dimensions du colis" className="mt-3 flex flex-wrap gap-2">
                            {[
                                { label: 'Longueur', value: packageLength },
                                { label: 'Largeur', value: packageWidth },
                                { label: 'Hauteur', value: packageHeight },
                            ].map(dimension => {
                                const complete = dimension.value != null && Number.isFinite(Number(dimension.value)) && Number(dimension.value) > 0
                                const Icon = complete ? CheckCircle2 : CircleDashed
                                return <li key={dimension.label} className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs" style={{
                                    background: complete ? (isDark ? '#132d24' : '#ecfdf5') : (isDark ? '#292117' : '#ffffffb3'),
                                    borderColor: complete ? (isDark ? '#265b45' : '#a7f3d0') : (isDark ? '#6b4b29' : '#fed7aa'),
                                    color: complete ? (isDark ? '#a7f3d0' : '#065f46') : (isDark ? '#fcd9a5' : '#92400e'),
                                }}>
                                    <Icon aria-hidden="true" size={13} className="shrink-0" />
                                    <span className="font-medium">{dimension.label}</span>
                                    <span>{complete ? presentation.format(dimension.value, unit, 'cargoLength') : 'À compléter'}</span>
                                </li>
                            })}
                        </ul>}
                    </div>
                    {onCompleteDimensions && layout.reason !== 'pallet-dimensions' && <button type="button" onClick={onCompleteDimensions}
                        className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
                        style={{ background: isDark ? '#fbbf24' : '#b45309', color: isDark ? '#292014' : '#ffffff' }}>
                        Compléter les dimensions<ArrowRight aria-hidden="true" size={15} />
                    </button>}
                </div>
            </div>}
            {layout.simplified && <p role="status" className="px-4 py-3 text-sm" style={{ color: isDark ? '#94a3b8' : '#475569' }}>
                Aperçu simplifié : {packagePositions.length} colis représentés sur {layout.totalPackages.toLocaleString('fr-FR')}. Les statistiques conservent le total calculé.
            </p>}


            <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-3 pt-2 sm:px-5">
                <div role="group" aria-label="Points de vue de la palette" className="inline-flex rounded-full bg-stone-100 p-1">
                    {[
                        { action: setViewIsometric, text: '3D', title: 'Vue isométrique', active: pitch !== 90 && !(yaw === 90 && pitch === 10) },
                        { action: setViewTop, text: 'Dessus', title: 'Vue de dessus', active: pitch === 90 },
                        { action: setViewSide, text: 'Côté', title: 'Vue de côté', active: yaw === 90 && pitch === 10 },
                    ].map(view => <button key={view.title} type="button" title={view.title} aria-pressed={view.active} onClick={() => { setAutoRotate(false); view.action() }} className={`rounded-full px-3 py-1.5 text-[11px] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${view.active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>{view.text}</button>)}
                </div>
                <div role="group" aria-label="Affichage de la palette" className="flex items-center gap-1">
                    <button type="button" title={showAnnotations ? 'Masquer dimensions' : 'Afficher dimensions'} aria-pressed={showAnnotations} onClick={() => setShowAnnotations(value => !value)} className={`flex h-8 w-8 items-center justify-center rounded-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${showAnnotations ? 'bg-stone-100 text-slate-800' : 'text-slate-400 hover:bg-stone-100'}`}>{showAnnotations ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                    <button type="button" title={autoRotate ? 'Arrêter rotation' : 'Rotation auto'} aria-pressed={autoRotate} onClick={() => setAutoRotate(value => !value)} className={`flex h-8 w-8 items-center justify-center rounded-lg transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${autoRotate ? 'bg-slate-900 text-white' : 'bg-stone-100 text-slate-600'}`}>{autoRotate ? <Pause size={14} /> : <Play size={14} />}</button>
                </div>
            </div>
            <div ref={canvasRef} className="relative h-[300px] sm:h-[340px]" style={{ background: svgBackground, touchAction: 'none', overflow: 'hidden', cursor: 'grab' }} onPointerDown={handlePointerDown} onContextMenu={event => event.preventDefault()}>
                <svg role="img" aria-label="Palette et colis en trois dimensions" width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="xMidYMid slice" style={{ display: 'block', width: '100%', height: '100%' }}>
                    {/* Grid floor */}
                    <defs>
                        <pattern id="pallet-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                            <path d="M 40 0 L 0 0 0 40" fill="none"
                                stroke={isDark ? 'rgba(100,116,139,0.12)' : 'rgba(148,163,184,0.14)'}
                                strokeWidth="0.5"
                            />
                        </pattern>
                    </defs>
                    <rect x="0" y="0" width={svgWidth} height={svgHeight} fill="url(#pallet-grid)" />

                    {/* Pallet faces */}
                    {showPallet && <g role="group" aria-label="Structure en bois de la palette">{sortedPalletFaces.map(face => {
                        const pts = face.indices.map(i => `${face.points[i][0]},${face.points[i][1]}`).join(' ')
                        const fillColor = adjustBrightness(palletColor, face.shade * face.part.tone)
                        const isHL = highlightedAttribute === 'dimensions' || highlightedAttribute === 'board'
                        return (
                            <g key={`${face.part.id}-${face.id}`}>
                            <polygon
                                points={pts}
                                fill={fillColor}
                                stroke={isHL ? '#38bdf8' : adjustBrightness(fillColor, 0.78)}
                                strokeWidth={isHL ? 1.6 : 0.45}
                                strokeLinejoin="round"
                            />
                            {face.id === 'top' && face.part.kind === 'deck' && <>
                                {[0, 1, 2].map(row => <polyline key={row} points={[0.04, 0.28, 0.54, 0.78, 0.96].map((step, index) => {
                                    const point = project(face.part.x + step * face.part.length,
                                        face.part.y + face.part.width * (0.18 + row * 0.29 + Math.sin(index * 1.8 + row) * 0.025), palletH)
                                    return `${point[0]},${point[1]}`
                                }).join(' ')} fill="none" stroke={adjustBrightness(palletColor, 0.5)} strokeWidth={0.45} opacity={0.24} />)}
                                {[0.08, 0.92].flatMap(x => [0.22, 0.78].map(y => {
                                    const point = project(face.part.x + face.part.length * x, face.part.y + face.part.width * y, palletH)
                                    return <circle key={`${x}-${y}`} cx={point[0]} cy={point[1]} r={0.65} fill={adjustBrightness(palletColor, 0.45)} opacity={0.55} />
                                }))}
                            </>}
                            </g>
                        )
                    })}</g>}

                    {/* Package faces */}
                    {showPackages && <g role="group" aria-label="Colis palettisés" data-appearance={useKraftColor ? 'carton' : 'color'}>{allPackageFaces.map((face, idx) => {
                        const pts = face.indices.map(i => `${face.pVerts[i][0]},${face.pVerts[i][1]}`).join(' ')
                        const tone = 0.98 + (face.ordinal % 5) * 0.01
                        const fillColor = adjustBrightness(face.color, face.shade * tone)
                        const isHL = highlightedAttribute === 'packages'
                        return (
                            <g key={`pkg-${idx}`}>
                            <polygon
                                points={pts}
                                fill={fillColor}
                                stroke={isHL ? '#38bdf8' : adjustBrightness(fillColor, 0.75)}
                                strokeWidth={isHL ? 2 : 0.4}
                                strokeLinejoin="round"
                            />
                            <CartonFaceDetails face={face.id} points={face.indices.map(index => face.pVerts[index])} detailed={packagePositions.length <= 64} inkColor={adjustBrightness(fillColor, 0.55)} />
                            </g>
                        )
                    })}</g>}

                    {/* Dimension arrows (redesigned) */}
                    {showAnnotations && dimensionLines.map((line, idx) => renderDimensionArrow(line as any, idx))}

                    {/* Max height ceiling plane */}
                    {ceilingOverlay}

                </svg>
                <div role="group" aria-label="Zoom de la palette" onPointerDown={event => event.stopPropagation()} className="absolute bottom-3 right-3 flex flex-col gap-1.5 rounded-xl border border-white bg-white/80 p-1.5 shadow-sm backdrop-blur-md sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2">
                    <button type="button" title="Zoom avant" aria-label="Zoom avant" disabled={zoom >= 2.5} onClick={() => setZoom(value => Math.min(2.5, value + 0.1))} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:opacity-30"><ZoomIn size={16} /></button>
                    <span className="py-1 text-center text-[9px] tabular-nums text-slate-400">{Math.round(zoom * 100)} %</span>
                    <button type="button" title="Zoom arrière" aria-label="Zoom arrière" disabled={zoom <= 0.8} onClick={() => setZoom(value => Math.max(0.8, value - 0.1))} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:opacity-30"><ZoomOut size={16} /></button>
                </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-[10px] text-slate-400 sm:px-5">
                <div className="flex gap-1.5">
                    {[
                        { visible: showPallet, toggle: () => setShowPallet(value => !value), color: palletColor, label: 'Palette', title: showPallet ? 'Masquer palette' : 'Afficher palette' },
                        { visible: showPackages, toggle: () => setShowPackages(value => !value), color: packageSurfaceColor, label: 'Colis', title: showPackages ? 'Masquer colis' : 'Afficher colis' },
                    ].map(item => <button key={item.label} type="button" title={item.title} aria-pressed={item.visible} onClick={item.toggle} className={`inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1.5 text-[10px] font-medium text-slate-600 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${item.visible ? '' : 'opacity-40'}`}><span className="h-2 w-2 rounded-sm" style={{ background: item.color }} />{item.label}</button>)}
                </div>
                <span className="inline-flex items-center gap-1.5" title="Clic gauche : rotation · clic droit : déplacement · roulette : zoom"><Move3d size={12} />Glisser pour orienter · roulette pour zoomer</span>
            </div>
            <div aria-label="Statistiques de la palette" className="grid grid-cols-2 gap-px border-t border-stone-100 bg-stone-100 xl:grid-cols-4">
                {[
                    { icon: Box, label: 'Colis / couche', value: layout.ready ? String(packagesPerLayer) : '—', hover: 'packages' },
                    { icon: Layers, label: 'Couches', value: layout.ready ? String(maxLayers) : '—', hover: 'packages' },
                    { icon: Weight, label: 'Poids estimé', value: presentation.format(totalWeight, 'kg', 'mass'), hover: 'weight' },
                    { icon: ArrowUp, label: 'Hauteur chargée', value: layout.ready ? presentation.format(palletH + maxLayers * pkgH, unit, 'cargoLength') : '—', hover: 'height' },
                ].map(stat => <div key={stat.label} onMouseEnter={() => handleAttributeHover(stat.hover)} onMouseLeave={handleAttributeLeave} className="min-w-0 bg-white px-3 py-4 transition hover:bg-stone-50"><div className="mb-2 flex items-center gap-1.5 text-[10px] text-slate-400"><stat.icon size={12} />{stat.label}</div><p className="break-words text-sm font-semibold tracking-tight text-slate-800">{stat.value}</p></div>)}
            </div>
        </div>
    )
}
